import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCheckInHabit,
  useUncheckHabit,
  newIdempotencyKey,
  adminKeys,
  familyKeys,
  type TodayHabitsResponse,
} from '@kidshome/api-contract';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `check-habit` — the optimistic core of the kid habit surface, mirroring `use-complete-task`. On a
 * one-tap check (or same-day uncheck) we:
 *   1. cancel the in-flight `habits/today` query + snapshot it,
 *   2. flip `doneToday` AND nudge the visible `currentStreak`/`bestStreak` in the cache (instant ✓),
 *   3. carry an `Idempotency-Key` so an offline replay (the PWA outbox) never double-applies,
 *   4. on error roll the cache back, and on settle invalidate so the server (authoritative)
 *      reconciles the real streak numbers.
 *
 * The streak math here is a KIND optimistic guess, never authoritative: a check bumps the current
 * streak by one (and best if it would exceed it); an uncheck steps it back by one (floored at 0). The
 * server owns the forgiving ADR-0028 rule (single-miss grace), so onSettled always reconciles — the
 * optimistic number only avoids a flash of stale "0" between tap and refetch. We NEVER show a streak
 * going negative or a "lost" state; the floor-at-0 keeps the copy encouraging.
 */
interface Snapshot {
  today: TodayHabitsResponse | undefined;
  key: readonly unknown[];
}

function applyCheck(
  today: TodayHabitsResponse,
  habitId: string,
  done: boolean,
): TodayHabitsResponse {
  return {
    ...today,
    habits: today.habits.map((h) => {
      if (h.id !== habitId) return h;
      if (h.doneToday === done) return h; // idempotent: already in the target state
      const currentStreak = done ? h.currentStreak + 1 : Math.max(0, h.currentStreak - 1);
      const bestStreak = Math.max(h.bestStreak, currentStreak);
      return { ...h, doneToday: done, currentStreak, bestStreak };
    }),
  };
}

export function useCheckHabitAction(
  /**
   * Whose today-view to optimistically flip + reconcile. Omit for the SELF kid surface
   * (`["habits","today"]`); pass a child id for a GUARDIAN acting on that child
   * (`["habits","today",childId]`) so the two never share a cache slot. The settle invalidation keys
   * off the same slot, and a bare `habitsToday()` prefix-invalidation elsewhere still reconciles both.
   */
  childId?: string,
): {
  /**
   * Check a habit in. Omit `localDate` for today's optimistic toggle; pass a past `YYYY-MM-DD` to
   * BACK-DATE a forgotten due day (ADR-0040) — the streak repairs from the server on settle (we don't
   * optimistically guess a back-dated streak, the server owns the forgiving rule). A back-dated check
   * is keyed by an idempotency key, so an offline replay records once.
   */
  check: (habitId: string, localDate?: string) => void;
  /**
   * Undo a check-in. Omit `localDate` for today's optimistic same-day undo; pass a past `YYYY-MM-DD`
   * to revoke a specific earlier day (a guardian correcting the calendar) — the server reconciles the
   * streak on settle (no optimistic guess for a back-dated undo).
   */
  uncheck: (habitId: string, localDate?: string) => void;
  /** True while THIS habit's check/uncheck is in flight (per-row pending). */
  isHabitPending: (habitId: string) => boolean;
  /**
   * Take (read-and-clear) the settled outcome for THIS habit's most recent check/uncheck, or null if it
   * hasn't settled yet or was already taken. Scoped per habit — unlike a single shared `error`, two
   * habits settling close together can never have one's outcome misattributed to the other (both verbs
   * share one `MutationObserver` per `HabitList`, so the observer's own `error` only ever reflects the
   * MOST RECENTLY STARTED call; see the `onSettled` below, which is called per-mutation by the
   * mutation cache itself and so stays correctly scoped even after the observer detaches).
   */
  takeSettledError: (habitId: string) => unknown;
  /** The last check/uncheck failure (so the caller can surface it), or null. Cleared on the next try. */
  error: unknown;
  /** Clear a stale check/uncheck failure (e.g. a sibling action on the same page just succeeded). */
  reset: () => void;
} {
  const qc = useQueryClient();
  const key = familyKeys.habitsToday(childId);

  // Track in-flight habit ids so each row's pending state is independent — a kid can tap several
  // habits without one in-flight write blocking the others. Each mutate keeps its own snapshot.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());
  // Per-habit settle outcome, populated from `onSettled` below (correctly scoped per call even when the
  // shared observer has moved on to a later habit) and drained by `takeSettledError`.
  const settledErrors = useRef<Map<string, unknown>>(new Map());

  const optimistic = (done: boolean) => ({
    onMutate: async ({
      habitId,
      localDate,
    }: {
      habitId: string;
      localDate?: string;
    }): Promise<Snapshot> => {
      setPendingIds((prev) => new Set(prev).add(habitId));
      await qc.cancelQueries({ queryKey: key });
      const today = qc.getQueryData<TodayHabitsResponse>(key);
      // Only flip TODAY's optimistic state for a same-day check. A BACK-DATED check (localDate set)
      // is for a past day — `doneToday` must not change; the server reconciles the repaired streak on
      // settle (the forgiving rule is the server's), so we leave the cache to refetch.
      if (today && localDate === undefined) {
        qc.setQueryData<TodayHabitsResponse>(key, applyCheck(today, habitId, done));
      }
      return { today, key };
    },
    onError: (_err: unknown, { habitId }: { habitId: string }, context?: Snapshot) => {
      if (!context?.today) return;
      // Roll back ONLY the affected habit's row from the snapshot — a wholesale snapshot restore
      // would clobber a concurrent optimistic flip on a sibling habit still in flight.
      const prev = context.today.habits.find((h) => h.id === habitId);
      if (!prev) return;
      qc.setQueryData<TodayHabitsResponse>(context.key, (cur) =>
        cur ? { ...cur, habits: cur.habits.map((h) => (h.id === habitId ? prev : h)) } : cur,
      );
    },
    onSettled: (_data: unknown, err: unknown, { habitId }: { habitId: string }) => {
      settledErrors.current.set(habitId, err ?? null);
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(habitId);
        return next;
      });
      // The server owns the forgiving streak rule — reconcile from the source of truth.
      void qc.invalidateQueries({ queryKey: key });
      // Reconcile the per-habit detail + day-by-day history too (same surfaces the back-dated
      // check-in path reconciles in use-backfill-habit.ts) — otherwise a habit detail page (guardian
      // or the kid's own) already mounted elsewhere keeps showing the pre-check streak/calendar.
      void qc.invalidateQueries({ queryKey: adminKeys.habit(habitId) });
      void qc.invalidateQueries({ queryKey: familyKeys.habitHistoryPrefix(habitId) });
      // A habit check-in may advance a cooperative quest objective (ADR-0032 — any habit/both source),
      // so reconcile the active family-quest card too — the additive bar never decreases on an uncheck,
      // and the refetch resolves the authoritative shared progress either way.
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      // The hero felt this habit: refresh bond ring / narrator so they react live (ADR-0095).
      invalidateHeroSurfaces(qc);
      // A check-in credits +6 ✨ effort stars (+ possible coin streak-bonuses) into the balance the Today
      // ✨/coin pill reads — invalidate it (broad prefix, no childId threaded here) so the reward feedback
      // shows immediately instead of holding the pre-credit value. The chore path (use-complete-task) does
      // the same.
      void qc.invalidateQueries({ queryKey: ['economy', 'balance'] });
    },
  });

  const checkIn = useCheckInHabit<Snapshot>(optimistic(true));
  const uncheckIn = useUncheckHabit<Snapshot>(optimistic(false));

  const check = useCallback(
    (habitId: string, localDate?: string) => {
      // Reset BOTH verbs so a stale uncheck failure doesn't keep the board's alert lit after a
      // successful check (the surfaced `error` is the union of the two mutations).
      checkIn.reset();
      uncheckIn.reset();
      checkIn.mutate({
        habitId,
        idempotencyKey: newIdempotencyKey(),
        ...(localDate ? { localDate } : {}),
      });
    },
    [checkIn, uncheckIn],
  );

  const uncheck = useCallback(
    (habitId: string, localDate?: string) => {
      checkIn.reset();
      uncheckIn.reset();
      uncheckIn.mutate({
        habitId,
        idempotencyKey: newIdempotencyKey(),
        ...(localDate ? { localDate } : {}),
      });
    },
    [checkIn, uncheckIn],
  );

  const isHabitPending = useCallback((habitId: string) => pendingIds.has(habitId), [pendingIds]);

  // Cross-reset hook for callers that render this hook's `error` into a banner SHARED with another
  // action (e.g. excuse-a-day on the guardian habits page): clearing both verbs lets the sibling
  // action reset us before it fires, so our stale failure never glows under its success.
  const reset = useCallback(() => {
    checkIn.reset();
    uncheckIn.reset();
  }, [checkIn, uncheckIn]);

  const takeSettledError = useCallback((habitId: string): unknown => {
    if (!settledErrors.current.has(habitId)) return null;
    const err = settledErrors.current.get(habitId);
    settledErrors.current.delete(habitId);
    return err;
  }, []);

  return {
    check,
    uncheck,
    isHabitPending,
    takeSettledError,
    error: checkIn.error ?? uncheckIn.error,
    reset,
  };
}
